import { InteractionRequiredAuthError } from '@azure/msal-browser';
import { env } from '@/config/env';
import { msalInstance } from '@/config/msal.instance';
import { api } from '@/services/api/client';
import type {
  PowerBiEmbedConfig,
  PowerBiEmbedConfigResponse,
  PowerBiListResponse,
  PowerBiWorkspaceReport,
} from './types';

/**
 * Power BI embedding service (POC).
 *
 * This module is the single swap point between the two embedding models. The
 * page/component only ever calls `getPowerBiEmbedConfig()` and receives a
 * ready-to-use {@link PowerBiEmbedConfig}. Which strategy runs is decided by
 * `VITE_POWERBI_EMBED_MODE`:
 *
 *  - `user-owns-data` (POC default) → {@link getUserOwnsDataConfig}: acquires an
 *    Azure AD token client-side via the app's existing MSAL instance. No backend.
 *  - `app-owns-data` → {@link getAppOwnsDataConfig}: fetches an embed token from
 *    the backend. No per-user Power BI licence. Requires a server endpoint.
 *
 * The Reports catalog ({@link listWorkspaceReports}) is user-owns-data only:
 * the browser lists the configured workspace with the same AAD token. Moving
 * to app-owns-data would need a backend listing endpoint as well.
 */

/**
 * Delegated scope required to read Power BI reports as the signed-in user.
 * The Entra app registration must have the "Power BI Service" delegated
 * permission granted/consented, otherwise MSAL throws `consent_required`.
 * The same scope covers listing a workspace's reports.
 */
const POWERBI_SCOPE =
  'https://analysis.windows.net/powerbi/api/Report.Read.All';

/** Power BI REST API root (user-owns-data calls go straight from the browser). */
const POWERBI_API_URL = 'https://api.powerbi.com/v1.0/myorg';

/** Backend endpoint that mints an embed token for the app-owns-data model. */
const EMBED_CONFIG_ENDPOINT = '/reports/embed-config';

/** Which report to build an embed config for. */
export interface EmbedConfigParams {
  reportId: string;
  /** Workspace (group) ID. Omit/empty for a "My workspace" report. */
  groupId?: string;
}

/**
 * Thrown by the silent-only paths when Power BI needs the user to sign in or
 * consent interactively. The UI turns it into an explicit "Connect Power BI"
 * action ({@link requestPowerBiAccess}) rather than redirecting on its own —
 * the catalog is fetched from the app shell on every page, and an automatic
 * redirect there would bounce users who lack consent out of the whole app.
 */
export class PowerBiInteractionRequiredError extends Error {
  constructor() {
    super('Power BI needs you to sign in before reports can be listed.');
    this.name = 'PowerBiInteractionRequiredError';
  }
}

/**
 * Build the standard Power BI embed URL for a report. When no groupId is given
 * (a personal "My workspace" report), it's omitted from the URL.
 * Used by the user-owns-data model where the frontend knows the IDs directly.
 */
function buildEmbedUrl(reportId: string, groupId?: string): string {
  const params = new URLSearchParams({ reportId });
  if (groupId) {
    params.set('groupId', groupId);
  }
  return `https://app.powerbi.com/reportEmbed?${params.toString()}`;
}

/**
 * Interactive fallback: redirect to Microsoft to sign in / consent to the
 * Power BI scope. Navigates away; the promise never resolves in practice.
 */
export async function requestPowerBiAccess(): Promise<void> {
  const account = msalInstance.getActiveAccount() ?? undefined;
  await msalInstance.acquireTokenRedirect({
    scopes: [POWERBI_SCOPE],
    account,
  });
}

/**
 * user-owns-data: acquire an AAD token for Power BI via the existing MSAL
 * instance, silent-first. When interaction is required, `interactive` decides
 * between redirecting (a user opened a report — mirrors useMsalAuth's
 * fallback) and surfacing {@link PowerBiInteractionRequiredError} (background
 * catalog fetch).
 */
async function acquirePowerBiAccessToken(
  interactive: boolean
): Promise<string> {
  const account = msalInstance.getActiveAccount();
  if (!account) {
    throw new Error('No active account — sign in before loading reports.');
  }

  try {
    const result = await msalInstance.acquireTokenSilent({
      scopes: [POWERBI_SCOPE],
      account,
    });
    return result.accessToken;
  } catch (error) {
    if (error instanceof InteractionRequiredAuthError) {
      if (!interactive) {
        throw new PowerBiInteractionRequiredError();
      }
      await requestPowerBiAccess();
      // acquireTokenRedirect navigates away; this line is unreachable.
      throw new Error('Power BI sign-in required.');
    }
    throw error;
  }
}

/**
 * Translate a failed Power BI REST call into a message support staff can act
 * on. 401/403 is by far the common case: the account has no Power BI licence
 * or no access to the workspace — nothing the portal can fix.
 */
function describePowerBiError(status: number, body: string): string {
  if (status === 401 || status === 403) {
    return (
      'Your account can’t access this Power BI workspace. You need a Power BI ' +
      'licence and Viewer access to the workspace — ask your Power BI admin.'
    );
  }
  if (status === 404) {
    return 'The configured Power BI workspace was not found.';
  }
  let detail = '';
  try {
    const parsed = JSON.parse(body) as { error?: { message?: string } };
    detail = parsed.error?.message ?? '';
  } catch {
    detail = body.slice(0, 200);
  }
  return `Power BI returned ${status}${detail ? `: ${detail}` : ''}`;
}

/**
 * user-owns-data: list every report in a workspace as the signed-in user
 * (`GET /groups/{groupId}/reports`). Power BI returns only what the user can
 * see, so this doubles as the access check. Silent auth only — see
 * {@link PowerBiInteractionRequiredError}.
 */
export async function listWorkspaceReports(
  groupId: string
): Promise<PowerBiWorkspaceReport[]> {
  if (!groupId) {
    throw new Error('Power BI workspace ID is not configured.');
  }
  if (env.VITE_POWERBI_EMBED_MODE === 'app-owns-data') {
    throw new Error(
      'Listing reports is not available in app-owns-data mode — it needs a backend endpoint.'
    );
  }

  const accessToken = await acquirePowerBiAccessToken(false);
  const response = await fetch(
    `${POWERBI_API_URL}/groups/${encodeURIComponent(groupId)}/reports`,
    { headers: { Authorization: `Bearer ${accessToken}` } }
  );

  if (!response.ok) {
    throw new Error(
      describePowerBiError(response.status, await response.text())
    );
  }

  const data =
    (await response.json()) as PowerBiListResponse<PowerBiWorkspaceReport>;
  return data.value ?? [];
}

/**
 * user-owns-data: embed config from an AAD token plus the standard embed URL.
 */
async function getUserOwnsDataConfig({
  reportId,
  groupId,
}: EmbedConfigParams): Promise<PowerBiEmbedConfig> {
  if (!reportId) {
    throw new Error('Power BI report ID is not configured.');
  }

  const accessToken = await acquirePowerBiAccessToken(true);

  return {
    reportId,
    embedUrl: buildEmbedUrl(reportId, groupId),
    accessToken,
    tokenType: 'Aad',
  };
}

/**
 * app-owns-data: fetch a backend-minted embed token. Not wired to a real
 * endpoint yet — implement `GET /reports/embed-config` server-side (it should
 * call the Power BI REST `GenerateToken` API with a service principal / master
 * user) and this becomes live.
 */
async function getAppOwnsDataConfig({
  reportId,
}: EmbedConfigParams): Promise<PowerBiEmbedConfig> {
  if (!reportId) {
    throw new Error('Power BI report ID is not configured.');
  }

  const response = await api.get<PowerBiEmbedConfigResponse>(
    EMBED_CONFIG_ENDPOINT,
    { params: { reportId } }
  );

  const data =
    (response.data as { data?: PowerBiEmbedConfigResponse }).data ??
    (response.data as unknown as PowerBiEmbedConfigResponse);

  return {
    reportId: data.reportId,
    embedUrl: data.embedUrl,
    accessToken: data.embedToken,
    tokenType: 'Embed',
  };
}

/**
 * Resolve a ready-to-use Power BI embed config for the configured model.
 * This is the only function the UI layer should call.
 */
export function getPowerBiEmbedConfig(
  params: EmbedConfigParams
): Promise<PowerBiEmbedConfig> {
  return env.VITE_POWERBI_EMBED_MODE === 'app-owns-data'
    ? getAppOwnsDataConfig(params)
    : getUserOwnsDataConfig(params);
}

export const reportsService = {
  getPowerBiEmbedConfig,
  listWorkspaceReports,
  requestPowerBiAccess,
};
